/*
 * Copyright (c) CIRROM 2025.
 */

import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSystemsAndProjects } from "../contexts/SystemsAndProjectsContext";

interface BreadcrumbData {
  systemOrProjectName?: string;
  parentSystemName?: string;
  parentSystemId?: number;
  integratedSystemId?: number;
}

/**
 * Hook that resolves breadcrumb data from URL params using the global context.
 * Automatically determines system/project name and parent system info for data projects.
 *
 * @param isDataProject - Whether the current page is for a data project
 * @returns BreadcrumbData containing resolved names and IDs for breadcrumb display
 *
 * @example
 * const breadcrumb = useBreadcrumbData(isDataProject);
 *
 * <PageTitle
 *   pageTitle="Contact Monitor Linkage | CIRROM"
 *   pageHeader="Contact Monitor Linkage"
 *   isDataProject={isDataProject}
 *   isMonitorPage
 *   systemOrProjectHeader={breadcrumb.systemOrProjectName}
 *   integratedSystemId={breadcrumb.integratedSystemId}
 *   parentSystemName={breadcrumb.parentSystemName}
 *   parentSystemId={breadcrumb.parentSystemId}
 * />
 */
export function useBreadcrumbData(isDataProject: boolean): BreadcrumbData {
  const { integratedSystemId, dataProjectId } = useParams();
  const { integratedSystems, dataProjects } = useSystemsAndProjects();

  return useMemo(() => {
    const id = dataProjectId || integratedSystemId;
    const parsedId = id ? Number.parseInt(id, 10) : undefined;

    if (isDataProject) {
      const project = dataProjects.find((p) => p.id === parsedId);
      const parentSystem = integratedSystems.find(
        (s) => s.id === project?.integrated_system_id
      );
      return {
        systemOrProjectName: project?.name,
        parentSystemName: parentSystem?.name,
        parentSystemId: parentSystem?.id,
        integratedSystemId: project?.integrated_system_id,
      };
    }

    const system = integratedSystems.find((s) => s.id === parsedId);
    return {
      systemOrProjectName: system?.name,
      integratedSystemId: parsedId,
    };
  }, [
    dataProjectId,
    integratedSystemId,
    isDataProject,
    integratedSystems,
    dataProjects,
  ]);
}
